import Store from '@ember-data/store';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type SelectOption } from 'donor-elf-web/lib/form-component-utils';
import type {
  BulkActionCustomSetPledgeCampaign,
  BulkActionResponsePledgeIds,
  BulkActionType,
} from 'donor-elf-web/models/bulk-action';
import PledgeModel from 'donor-elf-web/models/pledge';
import CampaignService from 'donor-elf-web/services/campaign-service';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import { validatePresence } from 'ember-changeset-validations/validators';

export type AssignToCampaignSavedArgs = { reloadedPledges: PledgeModel[] };

interface ComponentArgs {
  pledges: PledgeModel[];
  onSaved: (args: AssignToCampaignSavedArgs) => void;
  onClose: () => void;
}

const assignToCampaignValidation = {
  campaignId: validatePresence({ presence: true, message: "Can't be blank" }),
};

export default class PledgeBulkEditAssignToCampaignModalComponent extends Component<ComponentArgs> {
  constructor(owner: unknown, args: ComponentArgs) {
    super(owner, args);
    this.createChangeset();
  }

  @service declare campaignService: CampaignService;
  @service declare store: Store;

  @tracked changeset: any;
  @tracked showValidationErrors = false;
  @tracked bulkActionCustom?: BulkActionCustomSetPledgeCampaign;
  @tracked saving = false;
  @tracked saveError?: any;

  bulkActionType: BulkActionType = 'set_pledge_campaign';

  createChangeset() {
    this.changeset = new Changeset(
      {},
      lookupValidator(assignToCampaignValidation),
      assignToCampaignValidation,
    );
  }

  get restrictedCampaignIdsThatPledgesAreAssociatedWith() {
    let restrictedCampaignIds: string[] = [];
    let { campaignsById } = this.campaignService;

    for (let pledge of this.args.pledges) {
      for (let campaignPledge of pledge.campaignPledgesArray) {
        let campaign = campaignsById[campaignPledge.campaignId!];

        if (campaign?.restricted) {
          if (!restrictedCampaignIds.includes(campaign.id)) {
            restrictedCampaignIds.push(campaign.id);
          }
        }
      }
    }

    return restrictedCampaignIds;
  }

  get noPledges() {
    return this.args.pledges.length === 0;
  }

  get noCampaigns() {
    return this.campaignService.campaigns.length === 0;
  }

  get campaignSelectOptions(): SelectOption[] {
    return this.campaignService.sortedNonRestrictedCampaigns.map((campaign) => {
      return { val: campaign.id, label: campaign.label };
    });
  }

  get showCancelButton() {
    return !this.saving || this.saveError != null;
  }

  save = async () => {
    if (this.saving) {
      return;
    }

    this.showValidationErrors = false;
    await this.changeset.validate();

    if (this.changeset.get('isValid')) {
      let campaignId = this.changeset.get('campaignId');

      this.bulkActionCustom = {
        campaign_id: campaignId,
        pledge_ids: this.args.pledges.map((p) => p.id),
      };

      this.saving = true;
    } else {
      this.showValidationErrors = true;
      this.saving = false;
    }
  };

  onCompleted = async (response: BulkActionResponsePledgeIds) => {
    let pledgeIds = response.pledge_ids;
    let reloadedPledges = (
      await this.store.query('pledge', { filter: { ids: pledgeIds } })
    ).slice();

    this.campaignService.reloadActiveCampaignGoals();
    this.args.onSaved({ reloadedPledges });
  };

  onError = () => {
    this.saveError = true;
  };
}
